import React, { createContext, useContext, useEffect, useState } from "react";
import {
  addFrequentList as addFrequentListService,
  deleteFrequentList as deleteFrequentListService,
  fetchFrequentLists,
  updateFrequentLists,
  updateFrequentList as updateFrequentListService,
} from "../services/frequentItemService";
import { UserContext } from "./UserContext";
import i18next from "i18next";

export const FrequentListContext = createContext();

//TODO: Refacto
export const FrequentListProvider = ({ children }) => {
  const [frequentLists, setFrequentLists] = useState([]);
  const [suggestedList, setSuggestedList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useContext(UserContext);
  const userId = user?.uid;

  // Fetch frequent lists when the component mounts or userId changes
  useEffect(() => {
    if (!userId) return;

    const loadFrequentLists = async () => {
      try {
        setLoading(true);
        const data = await fetchFrequentLists(userId);
        setFrequentLists(Array.isArray(data) ? data : []);

        // Find a suggested list based on interval and lastUsed
        findSuggestedList(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadFrequentLists();
  }, [userId]);

  // Find a list to suggest based on interval and lastUsed date
  const findSuggestedList = (lists) => {
    const now = Date.now();

    // Find lists due for suggestion
    const dueLists = lists.filter((list) => {
      // If the list has never been used, it's not due
      if (list.lastUsed === null) return false;

      // Calculate when the list would be due
      const dueDate = list.lastUsed + list.interval * 24 * 60 * 60 * 1000; // Convert days to ms

      // List is due if the due date is in the past
      return dueDate <= now;
    });

    // Sort by how overdue they are (most overdue first)
    dueLists.sort((a, b) => {
      const aDueDate = a.lastUsed + a.interval * 24 * 60 * 60 * 1000;
      const bDueDate = b.lastUsed + b.interval * 24 * 60 * 60 * 1000;
      return aDueDate - bDueDate;
    });

    // Set the most overdue list as the suggested list
    setSuggestedList(dueLists.length > 0 ? dueLists[0] : null);
  };

  // Add a new frequent list
  const addFrequentList = async (newListData) => {
    if (!userId) return;

    try {
      // If we're passed a complete list, use it, otherwise create one
      const newList =
        typeof newListData === "object" && newListData.id
          ? newListData
          : {
              id: Date.now().toString(),
              name: newListData.name,
              interval: newListData.interval,
              lastUsed: null,
              nextSuggestion: null,
              shoppingListItems: [],
            };

      await addFrequentListService(userId, newList);

      // Update local state
      const updatedLists = [...frequentLists, newList];
      setFrequentLists(updatedLists);

      return newList.id;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  // Update a specific frequent list
  const updateFrequentList = async (listIdOrIndex, updatedList) => {
    if (!userId) return;
    try {
      // Get index if ID was passed
      let index = typeof listIdOrIndex === "number" ? listIdOrIndex : frequentLists.findIndex((list) => list.id === listIdOrIndex);

      if (index === -1) throw new Error("List not found");

      await updateFrequentListService(userId, index, updatedList);

      // Update local state
      const updatedLists = [...frequentLists];
      updatedLists[index] = {
        ...updatedList,
        lastUsed: updatedList.lastUsed || frequentLists[index].lastUsed, // Preserve lastUsed if not explicitly changed
      };
      setFrequentLists(updatedLists);

      // Check if suggestions need to be updated
      findSuggestedList(updatedLists);

      return updatedLists[index];
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  // Delete a frequent list
  const deleteFrequentList = async (listIdOrIndex) => {
    if (!userId) return;

    try {
      // Get index if ID was passed
      let index = typeof listIdOrIndex === "number" ? listIdOrIndex : frequentLists.findIndex((list) => list.id === listIdOrIndex);

      if (index === -1) throw new Error("List not found");

      await deleteFrequentListService(userId, index);

      // Update local state
      const updatedLists = frequentLists.filter((_, i) => i !== index);
      setFrequentLists(updatedLists);

      // Check if suggestions need to be updated
      findSuggestedList(updatedLists);
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  // Mark a list as used (updates lastUsed timestamp)
  const markListAsUsed = async (listIdOrIndex) => {
    if (!userId) return;

    try {
      // Get index if ID was passed
      let index = typeof listIdOrIndex === "number" ? listIdOrIndex : frequentLists.findIndex((list) => list.id === listIdOrIndex);

      if (index === -1) throw new Error("List not found");

      const list = frequentLists[index];
      const updatedList = {
        ...list,
        lastUsed: Date.now(),
        // Calculate next suggestion based on interval
        nextSuggestion: Date.now() + list.interval * 24 * 60 * 60 * 1000,
      };

      return await updateFrequentList(index, updatedList);
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  // Save all frequent lists at once
  const saveFrequentLists = async (updatedLists) => {
    if (!userId) return;

    try {
      await updateFrequentLists(userId, updatedLists);
      setFrequentLists(updatedLists);
      findSuggestedList(updatedLists);
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  // Add an item to a specific list
  const addItemToList = async (listId, item) => {
    if (!userId) return;

    try {
      // Check if we have any lists at all
      if (frequentLists?.length === 0) {
        // No lists available - create a default list and immediately add the item to it
        const t = i18next.t;
        
        const defaultList = {
          id: Date.now().toString(),
          name: t("frequentItems.defaultListName"),
          interval: 7,
          lastUsed: null,
          nextSuggestion: null,
          shoppingListItems: [item],
        };

        await addFrequentListService(userId, defaultList);

        setFrequentLists([defaultList]);

        return defaultList;
      }

      if (!listId) {
        // If no list ID provided, use the first list
        listId = frequentLists[0].id;
        console.log("No list ID provided, using first list:", listId);
      }

      const listIndex = frequentLists.findIndex((list) => list.id === listId);
      if (listIndex === -1) {
        console.error(
          `List with ID ${listId} not found. Available lists:`,
          frequentLists.map((l) => `${l.id}: ${l.name}`),
        );
        // Fallback to the first list if the specified list doesn't exist
        if (frequentLists.length > 0) {
          listId = frequentLists[0].id;
         
          const updatedListIndex = frequentLists.findIndex((list) => list.id === listId);
          const updatedList = { ...frequentLists[updatedListIndex] };

          // Initialize shoppingListItems if needed
          if (!updatedList.shoppingListItems) {
            updatedList.shoppingListItems = [];
          }

          // Add the new item
          updatedList.shoppingListItems.push(item);

          return await updateFrequentList(updatedListIndex, updatedList);
        } else {
          throw new Error("List not found and no fallback available");
        }
      }

      // Normal case - list exists
      const updatedList = { ...frequentLists[listIndex] };

      // Initialize shoppingListItems if needed
      if (!updatedList.shoppingListItems) {
        updatedList.shoppingListItems = [];
      }

      // Add the new item
      updatedList.shoppingListItems.push(item);

      return await updateFrequentList(listIndex, updatedList);
    } catch (err) {
      console.error("Error in addItemToList:", err);
      setError(err);
      throw err;
    }
  };

  // Update an item in a specific list
  const updateItemInList = async (listId, itemIndex, updatedItem) => {
    if (!userId) return;

    try {
      const listIndex = frequentLists.findIndex((list) => list.id === listId);
      if (listIndex === -1) throw new Error("List not found");

      const updatedList = { ...frequentLists[listIndex] };

      // Ensure array exists and index is valid
      if (!updatedList.shoppingListItems || itemIndex < 0 || itemIndex >= updatedList.shoppingListItems.length) {
        throw new Error("Item not found");
      }

      // Update the item
      updatedList.shoppingListItems[itemIndex] = updatedItem;

      return await updateFrequentList(listIndex, updatedList);
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  // Delete an item from a specific list
  const deleteItemFromList = async (listId, itemIndex) => {
    if (!userId) return;

    try {
      const listIndex = frequentLists.findIndex((list) => list.id === listId);
      if (listIndex === -1) throw new Error("List not found");

      const updatedList = { ...frequentLists[listIndex] };

      // Ensure array exists and index is valid
      if (!updatedList.shoppingListItems || itemIndex < 0 || itemIndex >= updatedList.shoppingListItems.length) {
        throw new Error("Item not found");
      }

      // Remove the item
      updatedList.shoppingListItems.splice(itemIndex, 1);

      return await updateFrequentList(listIndex, updatedList);
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const prepareItemsForCart = async (listId) => {
    if (!userId) return null;

    try {
      const listIndex = frequentLists.findIndex((list) => list.id === listId);
      if (listIndex === -1) throw new Error("List not found");

      const list = frequentLists[listIndex];

      // Ensure list has items
      if (!list.shoppingListItems || list.shoppingListItems.length === 0) {
        return [];
      }

      const shoppingItems = list.shoppingListItems.map((item) => {
        const recipeRef = {
          title: list.name, // Use list name as recipe title
        };

        return {
          ...item,
          recipes: [recipeRef],
        };
      });

      // Mark this list as used
      await markListAsUsed(listIndex);

      return shoppingItems;
    } catch (err) {
      console.error("Error preparing list items for cart:", err);
      setError(err);
      throw err;
    }
  };

  return (
    <FrequentListContext.Provider
      value={{
        frequentLists,
        suggestedList,
        loading,
        error,
        addFrequentList,
        updateFrequentList,
        deleteFrequentList,
        markListAsUsed,
        saveFrequentLists,
        findSuggestedList,
        addItemToList,
        updateItemInList,
        deleteItemFromList,
        prepareItemsForCart,
      }}
    >
      {children}
    </FrequentListContext.Provider>
  );
};

// Custom hook to use the FrequentListContext
export const useFrequentLists = () => {
  const context = useContext(FrequentListContext);
  if (context === undefined) {
    throw new Error("useFrequentLists must be used within a FrequentListProvider");
  }
  return context;
};
